import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, Search, X, MessageSquare, Users } from 'lucide-react';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const STATUS_KEYS = ['all', 'pending', 'reviewed', 'actioned', 'dismissed'];

const ModerationReportsTable = () => {
  const { t, i18n } = useTranslation('admin');
  const dateLocale = getDateFnsLocale(i18n.language);
  const { authToken } = useAuth();
  const { setLoading, setError, error, loading } = useAdminStore();
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [detailStatus, setDetailStatus] = useState('pending');
  const [saving, setSaving] = useState(false);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getModerationReports({
        token: authToken,
        status: statusFilter,
      });
      if (result.success) {
        setReports(result.reports || []);
      } else {
        setError(result.error || t('moderationTable.loadFailedFallback'));
      }
    } catch (err) {
      console.error('Moderation reports load error:', err);
      setError(getApiErrorMessage(err, { fallback: t('moderationTable.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authToken) loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, statusFilter]);

  const openDetail = (report) => {
    setSelected(report);
    setAdminNote(report.adminNote || '');
    setDetailStatus(report.status || 'pending');
  };

  const closeDetail = () => {
    setSelected(null);
    setAdminNote('');
  };

  const handleSave = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const result = await adminApi.updateModerationReport({
        token: authToken,
        reportId: selected.id,
        status: detailStatus,
        adminNote,
      });
      if (result.success && result.report) {
        setReports((prev) =>
          prev.map((r) => (r.id === result.report.id ? result.report : r))
        );
        setSelected(result.report);
      }
    } catch (err) {
      console.error('Update moderation report error:', err);
      setError(getApiErrorMessage(err, { fallback: t('moderationTable.updateFailedFallback') }));
    } finally {
      setSaving(false);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reports;
    return reports.filter((r) => {
      const hay = [
        r.reporterName,
        r.reporterEmail,
        r.reportedName,
        r.reportedEmail,
        r.reason,
        r.messageContentSnapshot,
        r.groupName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [reports, search]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'dd MMM yyyy, HH:mm', { locale: dateLocale });
    } catch {
      return String(dateString);
    }
  };

  const previewText = (report) => {
    const type = report.messageType || 'text';
    if (type === 'image') return t('moderationTable.previewImage');
    if (type === 'file') return report.messageFileName || t('moderationTable.previewFile');
    const raw = (report.messageContentSnapshot || '').replace(/<[^>]*>/g, ' ').trim();
    if (!raw) return t('moderationTable.previewEmpty');
    return raw.length > 80 ? `${raw.slice(0, 80)}…` : raw;
  };

  const statusBadge = (status) => {
    const map = {
      pending: 'bg-amber-500/15 text-amber-300 border-amber-500/25',
      reviewed: 'bg-blue-500/15 text-blue-300 border-blue-500/25',
      actioned: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
      dismissed: 'bg-slate-500/15 text-slate-300 border-slate-500/25',
    };
    return (
      <span
        className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${
          map[status] || map.pending
        }`}
      >
        {t(`moderationTable.statusBadge.${status}`, { defaultValue: status })}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <ShieldAlert className="w-6 h-6 text-amber-400" />
            {t('moderationTable.title')}
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('moderationTable.subtitle')}
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('moderationTable.searchPlaceholder')}
            className="w-full ps-9 pe-3 py-2 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#161f31] text-sm text-slate-900 dark:text-white"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#161f31] text-sm text-slate-900 dark:text-white"
        >
          {STATUS_KEYS.map((key) => (
            <option key={key} value={key}>
              {t(`moderationTable.statusFilter.${key}`)}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 dark:border-white/10 overflow-hidden bg-white dark:bg-[#0d1424]">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">{t('moderationTable.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">{t('moderationTable.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-white/[0.07] text-start text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <th className="px-4 py-3 font-medium text-start">{t('moderationTable.columns.date')}</th>
                  <th className="px-4 py-3 font-medium text-start">{t('moderationTable.columns.reporter')}</th>
                  <th className="px-4 py-3 font-medium text-start">{t('moderationTable.columns.reported')}</th>
                  <th className="px-4 py-3 font-medium text-start">{t('moderationTable.columns.reason')}</th>
                  <th className="px-4 py-3 font-medium text-start">{t('moderationTable.columns.preview')}</th>
                  <th className="px-4 py-3 font-medium text-start">{t('moderationTable.columns.scope')}</th>
                  <th className="px-4 py-3 font-medium text-start">{t('moderationTable.columns.status')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((report) => (
                  <tr
                    key={report.id}
                    onClick={() => openDetail(report)}
                    className="border-b border-slate-100 dark:border-white/[0.05] hover:bg-slate-50 dark:hover:bg-white/[0.03] cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300">
                      {formatDate(report.createdAt)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 dark:text-white">
                        {report.reporterName || '—'}
                      </div>
                      <div className="text-xs text-slate-500">{report.reporterEmail}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 dark:text-white">
                        {report.reportedName || report.senderNameSnapshot || '—'}
                      </div>
                      <div className="text-xs text-slate-500">
                        {report.reportedEmail || report.senderEmailSnapshot}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                      {t(`moderationTable.reasons.${report.reason}`, {
                        defaultValue: report.reason,
                      })}
                    </td>
                    <td className="px-4 py-3 max-w-[200px] truncate text-slate-600 dark:text-slate-400">
                      {previewText(report)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300">
                        {report.conversationType === 'group' ? (
                          <>
                            <Users className="w-3.5 h-3.5" />
                            {report.groupName || t('moderationTable.scopeGroup')}
                          </>
                        ) : (
                          <>
                            <MessageSquare className="w-3.5 h-3.5" />
                            {t('moderationTable.scopeDm')}
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3">{statusBadge(report.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selected && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000]"
              onClick={closeDetail}
            />
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 40 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-y-0 end-0 w-full max-w-lg z-[100001] bg-[#0d1424] border-s border-white/[0.08] shadow-2xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 flex items-center justify-between px-5 py-4 border-b border-white/[0.08] bg-[#0d1424]/95 backdrop-blur">
                <h4 className="text-base font-semibold text-white">{t('moderationTable.detailTitle')}</h4>
                <button
                  type="button"
                  onClick={closeDetail}
                  className="p-1.5 rounded-lg text-slate-400 hover:bg-white/[0.06]"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 space-y-5 text-sm">
                <section className="space-y-2">
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('moderationTable.detail.reporter')}
                  </h5>
                  <p className="text-white">{selected.reporterName || '—'}</p>
                  <p className="text-slate-400">{selected.reporterEmail}</p>
                </section>

                <section className="space-y-2">
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('moderationTable.detail.reported')}
                  </h5>
                  <p className="text-white">
                    {selected.reportedName || selected.senderNameSnapshot || '—'}
                  </p>
                  <p className="text-slate-400">
                    {selected.reportedEmail || selected.senderEmailSnapshot}
                  </p>
                </section>

                <section className="space-y-2">
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('moderationTable.detail.message')}
                  </h5>
                  <div className="rounded-xl border border-white/[0.08] bg-[#161f31] p-3 space-y-2">
                    <div className="flex flex-wrap gap-2 text-xs text-slate-400">
                      <span>
                        {t('moderationTable.detail.type')}: {selected.messageType || 'text'}
                      </span>
                      <span>·</span>
                      <span>{formatDate(selected.messageCreatedAt)}</span>
                      <span>·</span>
                      <span>
                        {selected.conversationType === 'group'
                          ? `${t('moderationTable.scopeGroup')}: ${selected.groupName || '—'}`
                          : t('moderationTable.scopeDm')}
                      </span>
                    </div>
                    <p className="text-slate-200 whitespace-pre-wrap break-words">
                      {(selected.messageContentSnapshot || '')
                        .replace(/<[^>]*>/g, ' ')
                        .trim() || t('moderationTable.previewEmpty')}
                    </p>
                    {selected.messageFileUrl && (
                      <p className="text-xs text-slate-500 break-all">
                        {selected.messageFileName || selected.messageFileUrl}
                      </p>
                    )}
                  </div>
                </section>

                <section className="space-y-1">
                  <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('moderationTable.detail.reason')}
                  </h5>
                  <p className="text-white">
                    {t(`moderationTable.reasons.${selected.reason}`, {
                      defaultValue: selected.reason,
                    })}
                  </p>
                  {selected.description && (
                    <p className="text-slate-400 mt-1">{selected.description}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-2">
                    {t('moderationTable.detail.reportedAt')}: {formatDate(selected.createdAt)}
                  </p>
                </section>

                <section className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('moderationTable.detail.status')}
                  </label>
                  <select
                    value={detailStatus}
                    onChange={(e) => setDetailStatus(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-white/10 bg-[#161f31] text-white text-sm"
                  >
                    {['pending', 'reviewed', 'actioned', 'dismissed'].map((key) => (
                      <option key={key} value={key}>
                        {t(`moderationTable.statusBadge.${key}`)}
                      </option>
                    ))}
                  </select>
                </section>

                <section className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {t('moderationTable.detail.adminNote')}
                  </label>
                  <textarea
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value.slice(0, 2000))}
                    rows={4}
                    placeholder={t('moderationTable.detail.adminNotePlaceholder')}
                    className="w-full rounded-xl border border-white/10 bg-[#161f31] px-3 py-2.5 text-sm text-white placeholder:text-slate-500 resize-none"
                  />
                  <p className="text-[11px] text-slate-500">{t('moderationTable.detail.noAutoAction')}</p>
                </section>

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeDetail}
                    className="px-4 py-2 rounded-xl text-sm text-slate-300 hover:bg-white/[0.06]"
                  >
                    {t('moderationTable.detail.close')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 rounded-xl text-sm font-semibold bg-[#3d8ef0] text-white hover:bg-[#5ca3f5] disabled:opacity-50"
                  >
                    {saving ? t('moderationTable.detail.saving') : t('moderationTable.detail.save')}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ModerationReportsTable;
